import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import type { EmployeeCostGroupAssignment } from '../services/costGroupService';
import { costGroupService } from '../services/costGroupService';
import { employeeService } from '../services/employeeService';
import {
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { EmployeeCostGroupAssignmentDrawer } from './EmployeeCostGroupAssignmentDrawer.tsx';

type DetailPanelMode = 'empty' | 'selected' | 'edit' | 'add';

export const EmployeeCostGroupAssignmentsSection: React.FC = () => {
  const queryClient = useQueryClient();
  const justSavedRef = useRef(false);
  const gridRef = useRef<AgGridReact>(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | null>(currentYear);
  const [panelMode, setPanelMode] = useState<DetailPanelMode>('empty');
  const [selectedAssignment, setSelectedAssignment] = useState<EmployeeCostGroupAssignment | null>(
    null
  );
  const [editingAssignment, setEditingAssignment] = useState<EmployeeCostGroupAssignment | null>(
    null
  );

  const { data: costGroups = [] } = useQuery({
    queryKey: ['cost-groups'],
    queryFn: () => costGroupService.getCostGroups(),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees-simple'],
    queryFn: () => employeeService.getActiveEmployeesSimple(),
  });

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['cost-group-assignments'],
    queryFn: () => costGroupService.getAllAssignments(),
  });

  const filteredAssignments = useMemo(() => {
    if (selectedYear == null) return [];
    return assignments.filter((a) => a.calendar_year === selectedYear);
  }, [assignments, selectedYear]);

  const createMutation = useMutation({
    mutationFn: ({
      employeeId,
      payload,
    }: {
      employeeId: string;
      payload: { cost_group: string; calendar_year: number };
    }) => costGroupService.createAssignment(employeeId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-group-assignments'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      employeeId,
      aid,
      payload,
    }: {
      employeeId: string;
      aid: string;
      payload: { cost_group: string; calendar_year: number };
    }) => costGroupService.updateAssignment(employeeId, aid, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-group-assignments'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ employeeId, aid }: { employeeId: string; aid: string }) =>
      costGroupService.deleteAssignment(employeeId, aid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-group-assignments'] });
    },
  });

  const getEmployeeName = useCallback(
    (employeeId: string) => {
      const emp = employees.find((e) => e.employee_id === employeeId);
      return emp?.name ?? employeeId;
    },
    [employees]
  );

  const handleRowClicked = useCallback((event: RowClickedEvent<EmployeeCostGroupAssignment>) => {
    if (!event.data) return;
    setSelectedAssignment(event.data);
    setPanelMode('selected');
    setEditingAssignment(null);
  }, []);

  const handleFormClose = () => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingAssignment(null);
      return;
    }
    setPanelMode((mode) => (mode === 'edit' ? 'selected' : 'empty'));
    setEditingAssignment(null);
  };

  const handleViewClose = () => {
    setPanelMode('empty');
    setSelectedAssignment(null);
    setEditingAssignment(null);
  };

  const handleDelete = (assignment: EmployeeCostGroupAssignment) => {
    if (window.confirm('Delete this assignment?')) {
      deleteMutation.mutate({ employeeId: assignment.employee_id, aid: assignment.id });
      setSelectedAssignment(null);
      setPanelMode('empty');
    }
  };

  const columnDefs: ColDef<EmployeeCostGroupAssignment>[] = useMemo(
    () => [
      {
        headerName: 'Employee',
        field: 'employee_id',
        width: 120,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
      },
      {
        headerName: 'Employee name',
        width: 300,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueGetter: (params) => {
          const id = params.data?.employee_id;
          if (!id) return '—';
          return getEmployeeName(id);
        },
      },
      {
        headerName: 'Group',
        field: 'cost_group',
        flex: 1,
        minWidth: 120,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
      },
    ],
    [getEmployeeName]
  );

  const gridOptions = useMemo(
    () => ({
      ...agGridDefaultOptions,
      theme: agGridTheme,
      onRowClicked: handleRowClicked,
      rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    }),
    [handleRowClicked]
  );

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <SettingsListDetailLayout
      detailOpen={detailOpen}
      detail={
        panelMode === 'edit' || panelMode === 'add' ? (
          <EmployeeCostGroupAssignmentDrawer
            isOpen={true}
            onClose={handleFormClose}
            assignment={editingAssignment}
            costGroups={costGroups}
            employees={employees}
            selectedYear={selectedYear}
            onSave={async (payload, context) => {
              if (context?.assignmentId) {
                await updateMutation.mutateAsync({
                  employeeId: context.employeeId,
                  aid: context.assignmentId,
                  payload,
                });
              } else if (context?.employeeId) {
                await createMutation.mutateAsync({ employeeId: context.employeeId, payload });
              }
              justSavedRef.current = true;
              setPanelMode('empty');
              setSelectedAssignment(null);
            }}
          />
        ) : panelMode === 'selected' && selectedAssignment ? (
          <EmployeeCostGroupAssignmentDrawer
            key={selectedAssignment.id}
            isOpen={true}
            readOnly
            assignment={selectedAssignment}
            costGroups={costGroups}
            employees={employees}
            onClose={handleViewClose}
            onSave={() => undefined}
            onEditRequest={() => {
              setEditingAssignment(selectedAssignment);
              setPanelMode('edit');
            }}
            onDeleteRequest={() => handleDelete(selectedAssignment)}
          />
        ) : null
      }
      list={
        <>
          <style>{agGridThemeStyles}</style>
          <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
            <div className="flex items-center">
              <label className="mr-2 text-sm text-text-secondary">Year:</label>
              <select
                className="mr-2 min-w-[120px] rounded-md border border-border-default bg-white p-2 text-sm text-text-primary"
                value={selectedYear ?? ''}
                onChange={(e) => setSelectedYear(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Select year</option>
                {Array.from({ length: 11 }, (_, i) => currentYear - 5 + i).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              onClick={() => {
                setEditingAssignment(null);
                setPanelMode('add');
              }}
            >
              <PlusIcon data-icon="inline-start" />
              Add assignment
            </Button>
          </div>
          {selectedYear != null &&
            (isLoading ? (
              <p className="text-text-secondary">Loading...</p>
            ) : (
              <div
                className="ag-theme-material min-h-0 flex-1"
                style={agGridSettingsContainerStyles}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={filteredAssignments}
                  columnDefs={columnDefs}
                  defaultColDef={agGridDefaultColDef}
                  gridOptions={gridOptions}
                  animateRows
                  domLayout="normal"
                  rowNumbers={true}
                  getRowId={(params) => params.data.id}
                />
              </div>
            ))}
        </>
      }
    />
  );
};
